<template>
    <!-- Only show spinner, since it spawns a popup and a picker as overlay -->
    <Spinner />
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import { mime } from "@/utils";
import { ZIndex } from "@primeuix/utils/zindex";
import config from "@/config";
import Spinner from "@/shared/Spinner.vue";
import { usePrimeVue } from "primevue/config";
import { useToast } from "primevue/usetoast";

/**
 * Google Drive File picker API reference: https://developers.google.com/picker/docs/reference
 * The `api.js` script introduces the `gapi` and `google` globals.
 */

const primevue = usePrimeVue();
const toast = useToast();
const picker = ref<google.picker.Picker>();

const emit = defineEmits<{
    (e: "files-selected", files: File[]): void;
    (e: "cancelled"): void;
}>();

withDefaults(
    defineProps<{
        mimeTypes?: string[];
    }>(),
    {
        mimeTypes: () => [
            "application/vnd.google-apps.document",
            "application/vnd.google-apps.drawing",
            "application/vnd.google-apps.presentation",
            "application/vnd.google-apps.spreadsheet",
        ],
    }
);

const pickerApiLoaded = ref(false);
const oAuthToken = ref<string | null>(null);

onMounted(() => {
    // Include the api.js from google. This introduces `gapi` and `google` globals.
    const script = document.createElement("script");
    script.setAttribute("async", "true");
    script.setAttribute("defer", "true");
    script.setAttribute("type", "text/javascript");
    script.setAttribute("src", "https://apis.google.com/js/api.js");
    script.onload = openPicker;

    document.head.appendChild(script);
});

const handleAuthResult = (authResult: gapi.auth2.AuthorizeResponse) => {
    if (authResult && !authResult.error) {
        oAuthToken.value = authResult.access_token;
        createPicker();
    } else {
        console.error(authResult);
        toast.add({
            severity: "warn",
            summary: "Logging in to Google failed.",
        });
        emit("cancelled");
    }
};

const waitForelement = <T extends Element>(selector: string) => {
    return new Promise<T>((resolve) => {
        const element = document.querySelector<T>(selector);
        if (element) {
            return resolve(element);
        }

        const observer = new MutationObserver(() => {
            if (element) {
                observer.disconnect();
                resolve(element);
            }
        });

        // If you get "parameter 1 is not of type 'Node'" error, see https://stackoverflow.com/a/77855838/492336
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    });
};

const createPicker = async () => {
    if (pickerApiLoaded.value && oAuthToken.value) {
        const browseView = new google.picker.DocsView().setIncludeFolders(true);

        try {
            picker.value = new google.picker.PickerBuilder()
                .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
                .addView(browseView)
                .setOAuthToken(oAuthToken.value)
                .setDeveloperKey(config.gDrivePicker.apiKey)
                .setCallback(pickerCallback)
                .setDocument(document)
                .build();

            picker.value.setVisible(true);

            const pickerElement =
                await waitForelement<HTMLElement>(".picker-dialog");
            if (!pickerElement) {
                throw new Error("Picker element not found.");
            }
            ZIndex.set("modal", pickerElement, primevue.config.zIndex?.modal);
        } catch (e) {
            console.error("Picker element not found");
            toast.add({
                severity: "error",
                summary: "Cannot open Google Drive Picker.",
                detail: e,
            });
        }
    }
};

const pickerCallback = (data: google.picker.ResponseObject) => {
    if (data[google.picker.Response.ACTION] === google.picker.Action.PICKED) {
        const files: File[] = [];
        data.docs.forEach((doc) => {
            const contents = JSON.stringify({
                "embedUrl": doc["embedUrl"],
                "url": doc["url"],
            });
            files.push(
                new File(
                    [contents],
                    `${doc.name}.${mime.getExtension(doc.mimeType)}`,
                    { type: `application/vnd.google-apps.${doc.type}` }
                )
            );
        });
        emit("files-selected", files);
    } else if (
        data[google.picker.Response.ACTION] === google.picker.Action.CANCEL
    ) {
        emit("cancelled");
    }
};

const openPicker = async () => {
    gapi.load("auth", async () => {
        gapi.auth2.authorize(
            {
                client_id: config.gDrivePicker.clientId,
                scope: config.gDrivePicker.scope,
            },
            handleAuthResult
        );
    });
    gapi.load("picker", () => {
        pickerApiLoaded.value = true;
        createPicker();
    });
};

onBeforeUnmount(() => {
    const pickerElement = document.querySelector<HTMLElement>(".picker-dialog");
    if (pickerElement) {
        ZIndex.clear(pickerElement);
    }
    picker.value?.dispose();
});
</script>
